import { DestroyRef, inject, Injectable, signal } from '@angular/core';

export const EMBED_PROTOCOL_VERSION = 1;
export const EMBED_WIDGET_SOURCE = 'palchat';
export const EMBED_HOST_SOURCE = 'palchat-host';

export type EmbedHostMessageType = 'open' | 'close';
export type EmbedPanelSize = 'default' | 'expanded' | 'full';

interface EmbedHostMessage {
  source: typeof EMBED_HOST_SOURCE;
  v: number;
  type: EmbedHostMessageType;
  isPaldeskHost?: boolean;
}

interface EmbedWidgetMessage {
  type: 'ready' | 'close' | 'size' | 'support';
  mode?: EmbedPanelSize;
  subject?: string;
}

function isEmbedHostMessage(value: unknown): value is EmbedHostMessage {
  if (typeof value !== 'object' || value === null) {
    return false;
  }

  return (
    'source' in value &&
    value.source === EMBED_HOST_SOURCE &&
    'v' in value &&
    value.v === EMBED_PROTOCOL_VERSION &&
    'type' in value &&
    (value.type === 'open' || value.type === 'close')
  );
}

/**
 * Bridges the widget to the host page's loader over postMessage.
 *
 * The host origin is unknown at startup, so `ready` is broadcast and later
 * messages go to whichever origin answered. The host owns panel size.
 */
@Injectable({ providedIn: 'root' })
export class EmbedBridgeService {
  private readonly _destroyRef = inject(DestroyRef);

  private readonly _$hostOrigin = signal<string | undefined>(undefined);
  private readonly _$size = signal<EmbedPanelSize>('default');
  private readonly _$isPaldeskHost = signal(false);

  readonly $hostOrigin = this._$hostOrigin.asReadonly();
  readonly $size = this._$size.asReadonly();
  readonly $isEmbedded = signal(window.parent !== window);

  /** Set from the handshake: the host page states whether it is PALDESK. */
  readonly $isPaldeskHost = this._$isPaldeskHost.asReadonly();

  constructor() {
    if (!this.$isEmbedded()) {
      return;
    }

    const listener = (event: MessageEvent) => {
      if (!isEmbedHostMessage(event.data)) {
        return;
      }
      this._$hostOrigin.set(event.origin);
      this._$isPaldeskHost.set(event.data.isPaldeskHost === true);
    };

    window.addEventListener('message', listener);
    this._destroyRef.onDestroy(() =>
      window.removeEventListener('message', listener),
    );
  }

  notifyReady(): void {
    this._post({ type: 'ready' }, '*');
  }

  requestClose(): void {
    this._post({ type: 'close' }, this._$hostOrigin() ?? '*');
  }

  requestSupport(subject: string): void {
    this._post({ type: 'support', subject }, this._$hostOrigin() ?? '*');
  }

  toggleExpanded(): void {
    this._resize(this._$size() === 'expanded' ? 'default' : 'expanded');
  }

  toggleFullScreen(): void {
    this._resize(this._$size() === 'full' ? 'default' : 'full');
  }

  private _resize(mode: EmbedPanelSize): void {
    this._$size.set(mode);
    this._post({ type: 'size', mode }, this._$hostOrigin() ?? '*');
  }

  private _post(message: EmbedWidgetMessage, targetOrigin: string): void {
    if (!this.$isEmbedded()) {
      return;
    }

    window.parent.postMessage(
      { source: EMBED_WIDGET_SOURCE, v: EMBED_PROTOCOL_VERSION, ...message },
      targetOrigin,
    );
  }
}
